import classNames from 'classnames';
import { type JSX, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { deliveryMethodMsg, t } from '../../../../common/i18n';
import { scrollTo } from '../../../../common/utils/browserUtils';
import { DeliveryType } from '../../../../generated/api/deliveryType';
import { Order } from '../../../../generated/api/order';
import {
  getBillingAccountDeliveryType,
  getBillingAccountNameForDeliveryType,
  getContactInfoLines,
  getDeliveryFields,
  getDeliveryMethodEntry,
} from '../../utils/placeOrderUtils';
import { StepError } from '../StepError';
import { StepButtons } from '../common/StepButtons';
import { ConfirmModal } from './ConfirmModal.js';
import { OrderInfo } from './OrderInfo';
import { SelectBillingAccount } from './SelectBillingAccount';
import { SelectPaymentMethod } from './SelectPaymentMethod';
import PaymentTypeEnum = Order.PaymentTypeEnum;
import type { InvoicingInfo } from './OrderInfo';
import type { BillingAccount } from '../../../../generated/api/billingAccount';
import type { ContactPerson } from '../../../../generated/api/contactPerson';
import type { PlaceOrderFormData } from '../../placeOrderTypes';

import './PaymentStep.scss';

export interface PaymentStepProps {
  mdmId: string;
  contactInfo: ContactPerson;
  isCardPaymentPossible: boolean;
  onCreatingNewBillingAccountChange: (isCreating: boolean) => void;
  orderSummary: JSX.Element;
  isSubmittingOrder?: boolean;
  onSubmit: () => void;
  onPrevious: () => void;
  deviceChangeBillingAccountId?: string;
}

export const PaymentStep = ({
  mdmId,
  contactInfo,
  isCardPaymentPossible,
  onCreatingNewBillingAccountChange,
  orderSummary,
  isSubmittingOrder = false,
  onSubmit,
  onPrevious,
  deviceChangeBillingAccountId,
}: PaymentStepProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();
  const [selectedBillingAccount, setSelectedBillingAccount] = useState<BillingAccount | undefined>();
  const [isCreatingNewBillingAccount, setIsCreatingNewBillingAccount] = useState(false);
  const [pendingPaymentType, setPendingPaymentType] = useState<PaymentTypeEnum | undefined>(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [paymentMethodKey, setPaymentMethodKey] = useState(0);
  const [paymentError, setPaymentError] = useState<string | undefined>();
  const delivery = watch('delivery');
  const paymentType = watch('payment.paymentType');
  const billingAccountId = watch('payment.billingAccountId');
  const deliveryType = watch('delivery.deliveryType');
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentTypeEnum>(
    paymentType || PaymentTypeEnum.BILLING_ACCOUNT
  );

  const isBillingAccountLocked = !!deviceChangeBillingAccountId;

  // Pre-select and lock billing account when device change dictates it
  useEffect(() => {
    if (deviceChangeBillingAccountId) {
      setValue('payment.billingAccountId', deviceChangeBillingAccountId);
      setValue('payment.paymentType', PaymentTypeEnum.BILLING_ACCOUNT);
      setSelectedPaymentType(PaymentTypeEnum.BILLING_ACCOUNT);
    }
  }, [deviceChangeBillingAccountId, setValue]);

  const validatePayment = () => {
    let isValid = true;

    const currentPaymentType = watch('payment.paymentType');
    const currentBillingAccountId = watch('payment.billingAccountId');

    if (currentPaymentType === PaymentTypeEnum.BILLING_ACCOUNT && !currentBillingAccountId) {
      setPaymentError(t.PUM6('Please select a billing account'));
      isValid = false;
    } else if (!currentPaymentType) {
      setPaymentError(t.CBBN('Please select a payment method'));
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = () => {
    if (validatePayment()) {
      onSubmit();
    }
    scrollTo(0);
  };

  const invoicingInfo: InvoicingInfo | undefined =
    selectedPaymentType === PaymentTypeEnum.BILLING_ACCOUNT && selectedBillingAccount
      ? {
          left: (
            <>
              <h4>{t.HVS2('Selected billing account')}</h4>
              <p>
                {selectedBillingAccount.payerBusinessId} {selectedBillingAccount.payerName}
                <br />
                {getBillingAccountNameForDeliveryType(selectedBillingAccount)}
              </p>
            </>
          ),
          right: (
            <>
              <h4>{t.XZH6(deliveryMethodMsg)}</h4>
              <p>{getBillingAccountDeliveryType(selectedBillingAccount)}</p>
            </>
          ),
        }
      : undefined;

  const resetCreatingNewState = () => {
    if (isCreatingNewBillingAccount) {
      setIsCreatingNewBillingAccount(false);
      onCreatingNewBillingAccountChange(false);
      scrollTo(0);
    }
  };

  const handleConfirmPaymentTypeChange = () => {
    setShowConfirmModal(false);
    setIsCreatingNewBillingAccount(false);
    onCreatingNewBillingAccountChange(false);
    if (pendingPaymentType) {
      setSelectedPaymentType(pendingPaymentType);
      setValue('payment.paymentType', pendingPaymentType);
      if (pendingPaymentType === PaymentTypeEnum.CARD) {
        setValue('payment.billingAccountId', '');
      }
      setPendingPaymentType(undefined);
      scrollTo(0);
    }
  };

  const handleCancelPaymentTypeChange = () => {
    setShowConfirmModal(false);
    setPendingPaymentType(undefined);
    // Force SelectPaymentMethod to re-render with the correct selected value
    setPaymentMethodKey(prev => prev + 1);
  };

  const deliveryInfo = deliveryType ? getDeliveryMethodEntry(deliveryType)?.description : undefined;
  const paymentMethodId = 'payment-method';
  const nothingToDeliver = deliveryType === DeliveryType.NONE;

  const deliveryInfoContent: JSX.Element | undefined = (() => {
    if (nothingToDeliver) {
      return (
        <>
          <h4>{t.R2DK('Nothing to deliver')}</h4>
          <p>{t.FPAP('Your order contains products that do not require delivery.')}</p>
        </>
      );
    }
    if (deliveryInfo) {
      return (
        <>
          <h4>{t.GPPB('Delivery information')}</h4>
          <p>{deliveryInfo}</p>
        </>
      );
    }
    return undefined;
  })();

  const deliveryFields =
    nothingToDeliver || !delivery?.deliveryAddress ? [] : getDeliveryFields(delivery.deliveryAddress);

  return (
    <div className={classNames('payment-step')}>
      <h1>{t.D9Q4('Payment')}</h1>
      <StepError
        className={dsClass.MARGIN_BOTTOM_4}
        errors={paymentError ? [{ text: paymentError, id: paymentMethodId }] : []}
      />
      <div id={paymentMethodId}>
        {isCardPaymentPossible && !isBillingAccountLocked && (
          <SelectPaymentMethod
            key={paymentMethodKey}
            label={t.CBBN('Choose payment method')}
            paymentType={selectedPaymentType}
            onChange={(p: PaymentTypeEnum) => {
              // Clear error when user makes a change
              setPaymentError(undefined);

              // If user is creating a new billing account and tries to switch payment type, show confirmation modal
              if (isCreatingNewBillingAccount) {
                setPendingPaymentType(p);
                setShowConfirmModal(true);
                return;
              }

              setSelectedPaymentType(p);
              setValue('payment.paymentType', p);
              if (p === PaymentTypeEnum.CARD) {
                setValue('payment.billingAccountId', '');
                resetCreatingNewState();
              }
            }}
          />
        )}
        {selectedPaymentType === PaymentTypeEnum.BILLING_ACCOUNT && (
          <SelectBillingAccount
            error={paymentError}
            mdmId={mdmId}
            onChange={(ba: BillingAccount) => {
              // Clear error when user makes a change
              setPaymentError(undefined);

              if (ba.billingAccountId) {
                setValue('payment.billingAccountId', ba.billingAccountId);
                setSelectedBillingAccount(ba);
                resetCreatingNewState();
              }
            }}
            onCreatingNewBillingAccount={() => {
              setIsCreatingNewBillingAccount(true);
              onCreatingNewBillingAccountChange(true);
            }}
            onFinishedCreatingNewBillingAccount={() => {
              resetCreatingNewState();
            }}
            billingAccountId={billingAccountId}
            disabled={isBillingAccountLocked}
            infoText={
              isCardPaymentPossible ? undefined : (
                <>
                  <h4>{t.PFPP('The Payment method for products with a monthly fee is always an invoice.')}</h4>
                  <p>{t.PSP3('Card payment is possible only for one-time fee devices and accessories.')}</p>
                </>
              )
            }
          />
        )}
      </div>
      {!isCreatingNewBillingAccount && (
        <>
          <OrderInfo
            mainTitle={t.FJW1('Review the order details')}
            orderContactInfo={{ fields: getContactInfoLines(contactInfo) }}
            deliveryInfo={{
              deliveryFields,
              deliveryInfo: deliveryInfoContent,
            }}
            invoicingInfo={invoicingInfo}
            showBackgroundColor={true}
          />
          <div className={classNames(dsClass.PADDING_BOTTOM_2)} />
          {orderSummary}
          <StepButtons onSubmit={handleSubmit} onPrevious={onPrevious} disableForward={isSubmittingOrder} />
        </>
      )}
      {showConfirmModal && (
        <ConfirmModal onStay={handleCancelPaymentTypeChange} onContinue={handleConfirmPaymentTypeChange} />
      )}
    </div>
  );
};
